"use client";

import { useActionState } from "react";
import { KeyRound, Mail } from "lucide-react";
import {
  logInMetCodeActie,
  vraagInloglinkAan,
  type AanvraagStaat,
  type CodeStaat,
} from "@/server/leden-acties";
import { PasskeyInloggen } from "@/components/passkey-knoppen";

// Inloggen op het Leden-gedeelte: lidnummer of e-mailadres, en dan een code
// van zes cijfers in de mail om hier in te typen. Zie server/leden-auth.ts.
// Wat er na het versturen staat is altijd hetzelfde, of we het lid kennen of
// niet; anders is uit te proberen welke lidnummers bestaan.
//
// De code en niet alleen de link: op een iPhone opent een link uit de mail in
// Safari, en dan sta je ingelogd in Safari en niet in de app op je beginscherm.
// De code typ je hier, in de app zelf.

const BEGIN: AanvraagStaat = { uitkomst: "leeg" };

export function InlogFormulier() {
  const [staat, verstuur, bezig] = useActionState(vraagInloglinkAan, BEGIN);

  if (staat.uitkomst === "verstuurd") return <CodeFormulier />;

  return (
    <div className="space-y-6">
      <PasskeyInloggen />
      <form action={verstuur} className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-foreground">Lidnummer of e-mailadres</span>
          <input
            name="wie"
            required
            autoComplete="username"
            inputMode="email"
            placeholder="Bijvoorbeeld 12345"
            className="w-full rounded-xl border border-border bg-card px-3 py-3 text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
        </label>
        {staat.uitkomst === "te-vaak" && (
          <p className="text-sm text-destructive">Dat ging net vaak achter elkaar. Probeer het over een kwartier opnieuw.</p>
        )}
        {staat.uitkomst === "ongeldig" && <p className="text-sm text-destructive">Vul je lidnummer of e-mailadres in.</p>}
        {staat.uitkomst === "dicht" && <p className="text-sm text-destructive">Inloggen kan op dit moment nog niet.</p>}
        <button
          type="submit"
          disabled={bezig}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-display font-black uppercase tracking-wide text-primary-foreground disabled:opacity-60"
        >
          <Mail className="size-4" aria-hidden />
          {bezig ? "Bezig..." : "Stuur me een inlogcode"}
        </button>
        <p className="text-xs text-muted-foreground">
          Geen wachtwoord nodig. Je lidnummer staat op je ledenpas en op de mails van NTTO.
        </p>
      </form>
    </div>
  );
}

const CODE_BEGIN: CodeStaat = { uitkomst: "leeg" };

function CodeFormulier() {
  const [staat, logIn, bezig] = useActionState(logInMetCodeActie, CODE_BEGIN);

  return (
    <form action={logIn} className="space-y-3">
      <div className="rounded-xl border border-border bg-card px-4 py-4">
        <p className="font-semibold text-foreground">Kijk in je mail</p>
        <p className="mt-1 text-sm text-soft-foreground">
          Als we je kennen, krijg je binnen een paar minuten een mail met een code van zes cijfers, op
          het adres dat bij NTTO bekend is. Vul hem hieronder in. Niets ontvangen? Kijk ook bij
          ongewenste mail.
        </p>
      </div>
      <label className="block">
        <span className="mb-1.5 block text-sm font-semibold text-foreground">Code uit de mail</span>
        <input
          name="code"
          required
          // Zo biedt een iPhone de code uit de Mail-app zelf aan boven het toetsenbord.
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern="[0-9 ]{6,7}"
          maxLength={7}
          placeholder="123456"
          className="w-full rounded-xl border border-border bg-card px-3 py-3 text-center font-display text-2xl font-black tracking-[0.4em] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
        />
      </label>
      {staat.uitkomst === "fout" && <p className="text-sm text-destructive">Die code klopt niet. Kijk hem nog eens na.</p>}
      {staat.uitkomst === "verlopen" && (
        <p className="text-sm text-destructive">
          Deze code werkt niet meer: verlopen, al gebruikt of te vaak fout. Vraag een nieuwe aan.
        </p>
      )}
      <button
        type="submit"
        disabled={bezig}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-display font-black uppercase tracking-wide text-primary-foreground disabled:opacity-60"
      >
        <KeyRound className="size-4" aria-hidden />
        {bezig ? "Bezig..." : "Inloggen"}
      </button>
      <a href="/leden" className="block text-center text-sm text-muted-foreground underline underline-offset-2">
        Nieuwe code aanvragen
      </a>
    </form>
  );
}
